import React, { useState, useContext, useEffect } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import GlobalData from "../../context/globaldata";
import Button from "../../components/Button/Button";
import { Input } from "../../components/Form";
import { ReactComponent as IconDanger } from "../../assets/images/icons/danger.svg";
import { ReactComponent as Logo } from "../../assets/images/logo-auth.svg";
import { ReactComponent as Throbber } from "../../assets/images/throbber.svg";

export default function AuthLogin() {
  const context = useContext(GlobalData);
  const location = useLocation();
  const history = useHistory();
  const search = location.search;
  const params = new URLSearchParams(search);
  const paramEmail = params.get("email");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [formFields, setFormFields] = useState({
    email: paramEmail || "",
    password: "",
  });

  const [errors, setErrors] = useState("");

  const updateFormFields = (field: string, data: string) => {
    setFormFields({
      ...formFields,
      [field]: data,
    });
  };

  const authenticateWithEmail = async (event: any) => {
    event.preventDefault();
    setIsLoading(true);
    setErrors("");

    if (!formFields.email || !formFields.password) {
      setErrors("Email and password fields are required.");
      return;
    }

    try {
      const userCredential = await context.firebaseApp
        .auth()
        .signInWithEmailAndPassword(formFields.email, formFields.password);

      const user = userCredential.user;

      if (window._pendingCred) {
        try {
          await user.linkWithCredential(window._pendingCred);
        } catch (e) {
          console.log(e);

          return;
        }
      }

      // Continue your normal login flow
      const userDoc = await context.firestore
        .collection("users")
        .doc(user.uid)
        .get();

      const userData = userDoc.data();

      if (userData && !userData.verified) {
        await userDoc.ref.update({ verified: true });
      }

      if (userData?.disabled) {
        setErrors("User is disabled. Contact admin.");
        return;
      }

      localStorage.setItem("expectSignIn", "true");
      window.location.href = "/";
    } catch (error: any) {
      console.error(error);
      setErrors(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const provider = new firebase.auth.OAuthProvider("microsoft.com");
  provider.setCustomParameters({
    tenant: "cf5120f9-2796-4515-8cda-c66e3675cf24",
  });

  useEffect(() => {
    context.firebaseApp.auth().onAuthStateChanged((user: any) => {
      if (user && !window._pendingCred) {
        window.location.href = "/";
      }
    });
  }, [context.firebaseApp]);

  useEffect(() => {
    context.firebaseApp
      .auth()
      .getRedirectResult()
      .then(async (result: any) => {
        if (result.user) {
          window.location.href = "/";
        }
      })
      .catch((error: any) => {
        console.log(error);

        if (error.code === "auth/account-exists-with-different-credential") {
          const pendingCred = (
            firebase.auth.OAuthProvider as any
          ).credentialFromError(error);
          const email = error.customData.email;

          window._pendingCred = pendingCred;

          setErrors(
            "This email already has a password-based account. Please sign in with your password once so we can link your Microsoft login."
          );
        } else {
          setErrors(error.message);
        }
      });
  }, [context.firebaseApp]);

  return (
    <>
      <Logo className="auth__logo" />
      <h1 className="auth__heading">Sign in to the portal</h1>

      {/* {process.env.NODE_ENV === "development" ? ( */}
      <>
        <form className="auth__form" onSubmit={authenticateWithEmail}>
          <Input
            value={formFields.email}
            type="email"
            placeholder="Email address"
            name="email"
            label=""
            onChange={(event: any) =>
              updateFormFields("email", event.target.value)
            }
            required
          />
          <Input
            value={formFields.password}
            type="password"
            placeholder="Password"
            name="password"
            label=""
            onChange={(event: any) =>
              updateFormFields("password", event.target.value)
            }
            aside={
              <Link className="input__aside" to="/reset-password">
                Forgot password?
              </Link>
            }
            required
          />
          {errors && (
            <div className="auth__errors">
              <IconDanger className="icon" />
              {errors}
            </div>
          )}
          <Button type="submit" disabled={isLoading}>
            {isLoading ? (
              <>
                <Throbber className="button__icon" />
                Signing in...
              </>
            ) : (
              "Sign in"
            )}
          </Button>
          <Link className="auth__sublink" to="/reset-password/">
            Forgot your password?
          </Link>
        </form>

        <div
          style={{
            marginTop: 8,
            marginBottom: 8,
          }}
        >
          --------------------OR--------------------
        </div>
      </>
      {/* // ) : null} */}
      <Button
        variant="ghost"
        onClick={async () => {
          try {
            await context.firebaseApp.auth().signInWithRedirect(provider);
          } catch (error) {
            console.error("Error signing in with Microsoft:", error);
          }
        }}
      >
        <img src="/microsoft.png" height={20} width={20} alt="Microsoft" />
        <span style={{ marginLeft: 8 }}>Log in with Microsoft</span>
      </Button>
    </>
  );
}
